import { motion } from "framer-motion";
import { ArrowRight, Home, Minus, Plus, X } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";

const SaveOnlyRegistrationPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Search state
  const [searchMobile, setSearchMobile] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [existingRegistration, setExistingRegistration] = useState(null);
  const [existingDirectory, setExistingDirectory] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isNewEntry, setIsNewEntry] = useState(false);

  // Add these new state variables near other useState declarations
  const [originalTotal, setOriginalTotal] = useState(0);
  const [showAdditionalPayment, setShowAdditionalPayment] = useState(false);
  const [additionalPaymentAmount, setAdditionalPaymentAmount] = useState(0);
  const [additionalPaymentLoading, setAdditionalPaymentLoading] = useState(false);

  // Form data state - UPDATED WITH NEW FIELDS
  const [formData, setFormData] = useState({
    name: "",
    batch: "",
    mobile: "",
    email: "",
    tshirtSize: "",
    accompany: {
      spouse: 0,
      guest: 0,
      driver: 0,
      childAbove5: 0,
      childBelow5: 0,
    },
    childAbove5TshirtSize: "",
    childBelow5TshirtSize: "",
    // New fields
    paymentStatus: "Pending",
    transactionId: "",
    paymentMethod: "",
    orderId: "",
    registrationDate: "",
    // method field will be automatically set to 2 in the submit function
  });

  const [errors, setErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [batchPricing, setBatchPricing] = useState({});
  const [tShirtSize, setTShirtSize] = useState([]);
  const [childAbove5TShirtSize, setChildAbove5TShirtSize] = useState([]);
  const [childBelow5TShirtSize, setChildBelow5TShirtSize] = useState([]);
  const [loading, setLoading] = useState(false);
  const [gender, setGender] = useState("Male");
  const [showChildAbove5TShirtModal, setShowChildAbove5TShirtModal] =
    useState(false);
  const [showChildBelow5TShirtModal, setShowChildBelow5TShirtModal] =
    useState(false);

  const API_BASE_URL = process.env.REACT_APP_API_ENTRYPOINT;
  const { user, getAuthHeader } = useAuth();

  // Store original mobile from search
  const [originalMobile, setOriginalMobile] = useState("");

  const stringToArray = (str) => {
    if (!str) return [];
    return str
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s);
  };

  const arrayToString = (arr) => {
    if (!arr || arr.length === 0) return "";
    return arr.join(",");
  };

  // Fetch batch pricing on mount
  useEffect(() => {
    const fetchBatchPricing = async () => {
      try {
        const data = await api.batchPricing.getAll();
        const pricingMap = {};
        data.forEach((bp) => {
          pricingMap[bp.batchName] = {
            participantFee: bp.participantFee,
            spouseFee: bp.spouseFee || 0,
            guestFee: bp.guestFee || 0,
            driverFee: bp.driverFee || 0,
            above5Fee: bp.above5Fee || 0,
            below5Fee: bp.below5Fee || 0,
          };
        });
        setBatchPricing(pricingMap);
      } catch (err) {
        console.error("Error fetching batch pricing:", err);
      }
    };

    fetchBatchPricing();
  }, []);

  // Fetch T-shirt sizes when gender changes
  useEffect(() => {
    if (!gender) return;

    const fetchTShirtSizes = async () => {
      try {
        const data = await api.lookup.getTShirtByGender(gender);
        const tShirtSizes = data.map((item) => item.name);
        setTShirtSize(tShirtSizes);
      } catch (err) {
        console.error("Error fetching t-shirt sizes:", err);
      }
    };

    fetchTShirtSizes();
  }, [gender]);

  // Fetch T-shirt sizes for children above 5
  useEffect(() => {
    const fetchChildAbove5TShirtSizes = async () => {
      try {
        const data = await api.lookup.getTShirtByGender("Child>5");
        const tShirtSizes = data.map((item) => item.name);
        setChildAbove5TShirtSize(tShirtSizes);
      } catch (err) {
        console.error(
          "Error fetching t-shirt sizes for children above 5:",
          err
        );
      }
    };

    fetchChildAbove5TShirtSizes();
  }, []);

  // Fetch T-shirt sizes for children below 5
  useEffect(() => {
    const fetchChildBelow5TShirtSizes = async () => {
      try {
        const data = await api.lookup.getTShirtByGender("Child<5");
        const tShirtSizes = data.map((item) => item.name);
        setChildBelow5TShirtSize(tShirtSizes);
      } catch (err) {
        console.error(
          "Error fetching t-shirt sizes for children below 5:",
          err
        );
      }
    };

    fetchChildBelow5TShirtSizes();
  }, []);

  const [searchParams] = useSearchParams();

  // Handle mobile parameter from URL
  useEffect(() => {
    const mobileParam = searchParams.get("mobile");
    if (mobileParam) {
      setSearchMobile(mobileParam);

      if (mobileParam.length === 11) {
        handleSearchRegistrationWithMobile(mobileParam);
      } else {
        setSearchError("Mobile number must be 11 digits");
      }
    }
  }, [searchParams]);

  // Search function with mobile parameter - UPDATED WITH NEW FIELDS
  const handleSearchRegistrationWithMobile = async (mobileNumber) => {
    console.log("Searching for mobile:", mobileNumber);

    if (!mobileNumber || mobileNumber.length < 11) {
      setSearchError("Please enter a valid 11-digit mobile number");
      return;
    }

    try {
      setSearchLoading(true);
      setSearchError("");
      setExistingRegistration(null);
      setExistingDirectory(null);
      setIsNewEntry(false);

      // First, search for existing registration
      const registrationResponse = await api.registration.getMyRegistrations(
        mobileNumber
      );

      if (registrationResponse && registrationResponse.length > 0) {
        // Found existing registration
        const registration = registrationResponse[0];
        setExistingRegistration(registration);

        // Format registration date if exists
        let formattedDate = "";
        if (registration.registrationDate) {
          const date = new Date(registration.registrationDate);
          formattedDate = date.toISOString().split('T')[0];
        }

        // Auto-fill form with registration data - INCLUDING NEW FIELDS
        setFormData({
          name: registration.name || "",
          batch: registration.batch || "",
          mobile: registration.mobile || mobileNumber,
          email: registration.email || "",
          tshirtSize: registration.tshirtSize || "",
          accompany: {
            spouse: registration.accompany?.spouse || 0,
            guest: registration.accompany?.guest || 0,
            driver: registration.accompany?.driver || 0,
            childAbove5: registration.accompany?.childAbove5 || 0,
            childBelow5: registration.accompany?.childBelow5 || 0,
          },
          childAbove5TshirtSize: registration.childAbove5TshirtSize || "",
          childBelow5TshirtSize: registration.childBelow5TshirtSize || "",
          // New fields
          paymentStatus: registration.paymentStatus || "Pending",
          transactionId: registration.transactionId || "",
          paymentMethod: registration.paymentMethod || "",
          orderId: registration.orderId || "",
          registrationDate: formattedDate || "",
        });

        setOriginalMobile(registration.mobile || mobileNumber);
        setGender(registration.gender || "Male");

        setShowForm(true);
        setIsNewEntry(false);

        // Check payment status - but still allow edits in save-only mode
        if (registration.paymentStatus === "Paid") {
          setSearchError(
            "This registration is already paid. You can still update it (save only, no payment)."
          );
        }

        return;
      }

      // If no registration found, search directory
      const directoryResponse = await api.directory.searchByMobile(
        mobileNumber
      );

      if (directoryResponse && directoryResponse.length > 0) {
        // Found existing directory - use as base for new registration
        const directory = directoryResponse[0];
        setExistingDirectory(directory);

        // Auto-fill form with directory data - INCLUDING NEW FIELDS
        setFormData({
          name: directory.name || "",
          batch: directory.batch || "",
          mobile: directory.mobile || mobileNumber,
          email: directory.email || "",
          tshirtSize: "",
          accompany: {
            spouse: 0,
            guest: 0,
            driver: 0,
            childAbove5: 0,
            childBelow5: 0,
          },
          childAbove5TshirtSize: "",
          childBelow5TshirtSize: "",
          // New fields - empty for new registration
          orderId: "",
          transactionId: "",
          paymentMethod: "",
          registrationDate: new Date().toISOString().split('T')[0], // Default to today
        });

        setOriginalMobile(directory.mobile || mobileNumber);
        setShowForm(true);
        setIsNewEntry(true);
      } else {
        // No existing record found - start fresh
        setFormData({
          name: "",
          batch: "",
          mobile: mobileNumber,
          email: "",
          tshirtSize: "",
          accompany: {
            spouse: 0,
            guest: 0,
            driver: 0,
            childAbove5: 0,
            childBelow5: 0,
          },
          childAbove5TshirtSize: "",
          childBelow5TshirtSize: "",
          // New fields - empty for new registration
          orderId: "",
          transactionId: "",
          paymentMethod: "",
          registrationDate: new Date().toISOString().split('T')[0], // Default to today
        });

        setOriginalMobile(mobileNumber);
        setShowForm(true);
        setIsNewEntry(true);
      }
    } catch (error) {
      console.error("Error searching:", error);
      setSearchError("Failed to search. Please try again.");
    } finally {
      setSearchLoading(false);
    }
  };

  // Original search function
  const handleSearchRegistration = async () => {
    await handleSearchRegistrationWithMobile(searchMobile);
  };

  const handleResetSearch = () => {
    setSearchMobile("");
    setExistingRegistration(null);
    setExistingDirectory(null);
    setShowForm(false);
    setIsNewEntry(false);
    setFormData({
      name: "",
      batch: "",
      mobile: "",
      email: "",
      tshirtSize: "",
      accompany: {
        spouse: 0,
        guest: 0,
        driver: 0,
        childAbove5: 0,
        childBelow5: 0,
      },
      childAbove5TshirtSize: "",
      childBelow5TshirtSize: "",
      // New fields
      orderId: "",
      transactionId: "",
      paymentMethod: "",
      registrationDate: "",
    });
    setOriginalMobile("");
    setGender("Male");
    setSearchError("");
    setShowSuccess(false);
    setShowError(false);
  };

  // Ensure mobile field is always set to originalMobile
  useEffect(() => {
    if (originalMobile) {
      setFormData((prev) => ({
        ...prev,
        mobile: originalMobile,
      }));
    }
  }, [originalMobile]);

  const handleInputChange = (field, value) => {
    if (field === "mobile") {
      return; // Don't allow mobile to be changed
    }

    setFormData((prev) => ({ ...prev, [field]: value }));

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleAccompanyChange = (type, inc) => {
    const newCount = Math.max(0, formData.accompany[type] + inc);

    setFormData((prev) => {
      // Only child categories have t-shirt size options
      let sizeField = "";
      if (type === "childAbove5") {
        sizeField = "childAbove5TshirtSize";
      } else if (type === "childBelow5") {
        sizeField = "childBelow5TshirtSize";
      }

      if (sizeField) {
        const currentSizes = stringToArray(prev[sizeField]);
        let updatedSizes = currentSizes;

        if (newCount < prev.accompany[type]) {
          // Decreasing count - remove last size
          updatedSizes = currentSizes.slice(0, newCount);
        } else if (newCount === 0) {
          updatedSizes = [];
        }

        return {
          ...prev,
          accompany: {
            ...prev.accompany,
            [type]: newCount,
          },
          [sizeField]: arrayToString(updatedSizes),
        };
      } else {
        // For spouse, guest, driver - no t-shirt size selection needed
        return {
          ...prev,
          accompany: {
            ...prev.accompany,
            [type]: newCount,
          },
        };
      }
    });
  };

  const handleTShirtSizeModalSelect = (type, size, action) => {
    const currentSizes = stringToArray(formData[type]);
    const maxSelections =
      formData.accompany[
        type === "childAbove5TshirtSize" ? "childAbove5" : "childBelow5"
      ];

    // Count occurrences of the selected size
    const sizeCount = currentSizes.filter((s) => s === size).length;

    let newSizes = [...currentSizes];

    if (action === "increase") {
      // Increase count if we haven't reached max selections
      if (currentSizes.length < maxSelections) {
        newSizes.push(size);
      }
    } else if (action === "decrease") {
      // Decrease count if this size exists
      if (sizeCount > 0) {
        const indexToRemove = newSizes.lastIndexOf(size);
        if (indexToRemove > -1) {
          newSizes.splice(indexToRemove, 1);
        }
      }
    } else if (action === "remove") {
      // Remove all occurrences of this size
      newSizes = newSizes.filter((s) => s !== size);
    }

    setFormData((prev) => ({
      ...prev,
      [type]: arrayToString(newSizes),
    }));
  };

  const handleClearSelection = (type) => {
    setFormData((prev) => ({
      ...prev,
      [type]: "",
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";

    // Mobile validation - always use originalMobile
    const mobileToValidate = originalMobile || formData.mobile;
    if (!mobileToValidate || !mobileToValidate.trim()) {
      newErrors.mobile = "Mobile number is required";
    } else if (!/^\d{11}$/.test(mobileToValidate)) {
      newErrors.mobile = "Must be 11 digits";
    }

    if (!formData.batch) newErrors.batch = "Batch/Group is required";

    // Validate payment fields if payment method is selected
    if (formData.paymentMethod) {
    //   if (!formData.orderId) {
    //     newErrors.orderId = "Order ID is required when payment method is selected";
    //   }
      if (!formData.transactionId) {
        newErrors.transactionId = "Transaction ID is required when payment method is selected";
      }
      if (!formData.orderId) {
        newErrors.orderId = "Payment person is required when payment method is selected";
      }
    }

    // Validate child above 5 t-shirt sizes
    const childAbove5Sizes = stringToArray(formData.childAbove5TshirtSize);
    if (
      formData.accompany.childAbove5 > 0 &&
      childAbove5Sizes.length !== formData.accompany.childAbove5
    ) {
      newErrors.childAbove5TshirtSize = `Please select ${formData.accompany.childAbove5} t-shirt size(s) for ${formData.accompany.childAbove5} child(ren) above 5`;
    }

    // Validate child below 5 t-shirt sizes
    const childBelow5Sizes = stringToArray(formData.childBelow5TshirtSize);
    if (
      formData.accompany.childBelow5 > 0 &&
      childBelow5Sizes.length !== formData.accompany.childBelow5
    ) {
      newErrors.childBelow5TshirtSize = `Please select ${formData.accompany.childBelow5} t-shirt size(s) for ${formData.accompany.childBelow5} child(ren) below 5`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const calculateTotal = () => {
    const batch = batchPricing[formData.batch || "Arts"];
    if (!batch) return 0;

    return (
      batch.participantFee +
      formData.accompany.spouse * (batch.spouseFee || 0) +
      formData.accompany.guest * (batch.guestFee || 0) +
      formData.accompany.driver * (batch.driverFee || 0) +
      formData.accompany.childAbove5 * (batch.above5Fee || 0) +
      formData.accompany.childBelow5 * (batch.below5Fee || 0)
    );
  };

  // Track total amount changes for additional payment
  useEffect(() => {
    if (existingRegistration?.paymentStatus === "Success") {
      const currentTotal = calculateTotal();
      const original = existingRegistration.totalAmount || 0;
      
      if (currentTotal > original) {
        setAdditionalPaymentAmount(currentTotal - original);
        setShowAdditionalPayment(true);
      } else {
        setShowAdditionalPayment(false);
        setAdditionalPaymentAmount(0);
      }
    }
  }, [formData.accompany, existingRegistration]);

  const handleSaveOnly = async () => {
    const mobileToUse = originalMobile || formData.mobile;

    if (!validateForm()) {
      console.log("Validation failed with errors:", errors);
      return;
    }

    if (!formData.name || !formData.name.trim()) {
      alert("Please enter a name");
      return;
    }

    if (!mobileToUse || !/^\d{11}$/.test(mobileToUse)) {
      alert("Please ensure mobile number is 11 digits");
      return;
    }

    setLoading(true);
    setShowError(false);
    setErrorMessage("");

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("name", formData.name);
      formDataToSend.append("batch", formData.batch);
      formDataToSend.append("mobile", mobileToUse);
      formDataToSend.append("email", formData.email);
      formDataToSend.append("tshirtSize", formData.tshirtSize);
      formDataToSend.append("gender", gender);
      formDataToSend.append("accompanySpouse", formData.accompany.spouse);
      formDataToSend.append("accompanyGuest", formData.accompany.guest);
      formDataToSend.append("accompanyDriver", formData.accompany.driver);
      formDataToSend.append(
        "accompanyChildAbove5",
        formData.accompany.childAbove5
      );
      formDataToSend.append(
        "accompanyChildBelow5",
        formData.accompany.childBelow5
      );
      formDataToSend.append(
        "childAbove5TshirtSize",
        formData.childAbove5TshirtSize || ""
      );
      formDataToSend.append(
        "childBelow5TshirtSize",
        formData.childBelow5TshirtSize || ""
      );

      // Add payment status as PENDING (no payment)
      formDataToSend.append("paymentStatus", formData.paymentStatus || "Pending");
      
      // Add new fields
      formDataToSend.append("orderId", formData.orderId || "");
      formDataToSend.append("transactionId", formData.transactionId || "");
      formDataToSend.append("paymentMethod", formData.paymentMethod || "");
      formDataToSend.append("registrationDate", formData.registrationDate || new Date().toISOString().split('T')[0]);
      
      // Set method to 2 automatically (as requested)
      formDataToSend.append("method", "2");

      if (user?.id) {
        formDataToSend.append("userId", user.id.toString());
      }

      let data;
      if (existingRegistration && existingRegistration.id) {
        // Update existing registration
        data = await api.registration.update(
          existingRegistration.id,
          formDataToSend
        );
      } else {
        // Create new registration
        data = await api.registration.create(formDataToSend);
      }

      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 5000);
      
      // Refresh the existing registration data
      if (data && data.id) {
        const updatedRegistration = await api.registration.getById(data.id);
        setExistingRegistration(updatedRegistration);
        
        // Update form with the returned data
        if (updatedRegistration.registrationDate) {
          const date = new Date(updatedRegistration.registrationDate);
          updatedRegistration.registrationDate = date.toISOString().split('T')[0];
        }
        
        setFormData({
          name: updatedRegistration.name || "",
          batch: updatedRegistration.batch || "",
          mobile: updatedRegistration.mobile || mobileToUse,
          email: updatedRegistration.email || "",
          tshirtSize: updatedRegistration.tshirtSize || "",
          accompany: {
            spouse: updatedRegistration.accompany?.spouse || 0,
            guest: updatedRegistration.accompany?.guest || 0,
            driver: updatedRegistration.accompany?.driver || 0,
            childAbove5: updatedRegistration.accompany?.childAbove5 || 0,
            childBelow5: updatedRegistration.accompany?.childBelow5 || 0,
          },
          childAbove5TshirtSize: updatedRegistration.childAbove5TshirtSize || "",
          childBelow5TshirtSize: updatedRegistration.childBelow5TshirtSize || "",
          orderId: updatedRegistration.orderId || "",
          transactionId: updatedRegistration.transactionId || "",
          paymentMethod: updatedRegistration.paymentMethod || "",
          registrationDate: updatedRegistration.registrationDate || "",
        });
      }

      setShowSuccess(true);
    
    // Redirect to /auth/admin after a short delay
    setTimeout(() => {
      // navigate("/auth/admin");
      navigate("/admin");

    }, 1500); 

      return data;
    } catch (error) {
      console.error("Error saving registration:", error);
      setShowError(true);
      setErrorMessage(error.message || "Failed to save registration");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleHomeClick = () => {
    navigate("/home");
  };

//   const handleViewRegistrations = () => {
//     navigate("/participants");
//   };

  // Updated accompany categories with proper labels and pricing
  const accompanyCategories = [
    { key: "spouse", label: "Spouse", showTshirt: false },
    { key: "childAbove5", label: "Children Above 5", showTshirt: true },
    { key: "childBelow5", label: "Children Below 5", showTshirt: true },
    { key: "guest", label: "Guest", showTshirt: false },
    { key: "driver", label: "Driver", showTshirt: false },
  ];

  // Payment method options
  const paymentMethodOptions = [
    { value: "", label: "Select Payment Method" },
    { value: "CASH", label: "Cash" },
    { value: "BKASH", label: "bKash" },
    { value: "NAGAD", label: "Nagad" },
    { value: "ROCKET", label: "Rocket" },
    { value: "BANK", label: "Bank Transfer" },
    { value: "CARD", label: "Card Payment" },
    { value: "OTHER", label: "Other" },
  ];

  const TShirtSizeModal = ({
    isOpen,
    onClose,
    onSelect,
    selectedSizesStr,
    title,
    maxSelections,
    availableSizes,
    onClear,
  }) => {
    if (!isOpen) return null;

    const selectedSizes = stringToArray(selectedSizesStr);

    // Calculate count of each selected size
    const sizeCounts = selectedSizes.reduce((acc, size) => {
      acc[size] = (acc[size] || 0) + 1;
      return acc;
    }, {});

    const totalSelected = selectedSizes.length;
    const remainingToSelect = maxSelections - totalSelected;

    return (
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-42">
        <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-2xl p-6 w-full max-w-md relative">
          {/* Close button - Top right */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-red-500 hover:text-gray-700 transition-colors"
            aria-label="Close modal"
          >
            <X size={24} />
          </button>

          <h3 className="text-xl font-bold text-gray-800 mb-2 pr-8">{title}</h3>
          <div className="mb-4">
            <p className="text-gray-600">
              Select <span className="font-bold">{maxSelections}</span> -
              Remaining: <span className="font-bold">{remainingToSelect}</span>
            </p>
            <div>
              <span>
                (Click <span className="font-medium text-green-600">+</span> to
                add a size and 
                <span className="font-medium text-red-600"> -</span> to remove a
                size)
              </span>
            </div>
          </div>

          {/* Clear button */}
          {selectedSizes.length > 0 && (
            <div className="mb-4 flex justify-end">
              <button
                onClick={onClear}
                className="px-3 py-1 text-sm bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition"
              >
                Clear All
              </button>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 mb-6">
            {availableSizes.map((size) => {
              const selectedCount = sizeCounts[size] || 0;
              const canIncrease = remainingToSelect > 0;
              const canDecrease = selectedCount > 0;

              return (
                <div
                  key={size}
                  className={`p-3 rounded-xl font-medium transition-all ${
                    selectedCount > 0
                      ? "bg-gradient-to-br from-green-500 to-blue-500 text-white shadow-lg"
                      : "bg-white text-gray-700 border border-gray-200"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="font-bold text-lg">{size}</span>
                      {selectedCount > 0 && (
                        <span className="text-white font-bold">
                          × {selectedCount}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      {/* Decrease Button */}
                      <button
                        onClick={() => onSelect(size, "decrease")}
                        disabled={!canDecrease}
                        className={`p-1 rounded-full ${
                          canDecrease
                            ? "bg-red-100 text-red-600 hover:bg-red-200"
                            : "bg-gray-100 text-gray-400 cursor-not-allowed"
                        }`}
                        title="Decrease"
                      >
                        <Minus size={18} />
                      </button>

                      {/* Increase Button */}
                      <button
                        onClick={() => onSelect(size, "increase")}
                        disabled={!canIncrease}
                        className={`p-1 rounded-full ${
                          canIncrease
                            ? "bg-green-100 text-green-600 hover:bg-green-200"
                            : "bg-gray-100 text-gray-400 cursor-not-allowed"
                        }`}
                        title="Increase"
                      >
                        <Plus size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex justify-between items-center mb-6">
            <div className="text-sm text-gray-600">
              Selected: <span className="font-bold">{totalSelected}</span>/
              {maxSelections}
            </div>
            {selectedSizes.length > 0 && (
              <div className="text-sm text-gray-700 max-w-[60%]">
                <span className="font-medium">Summary:</span>{" "}
                {Object.entries(sizeCounts)
                  .map(([size, count]) => `${size} (${count})`)
                  .join(", ")}
              </div>
            )}
          </div>

          <div className="flex justify-center">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg hover:opacity-90 transition"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderSizeButton = (type) => {
    const sizeField =
      type === "childAbove5"
        ? "childAbove5TshirtSize"
        : "childBelow5TshirtSize";
    const sizesStr = formData[sizeField];
    const sizes = stringToArray(sizesStr);
    const count = formData.accompany[type];

    if (count === 0) return null;

    // Calculate count of each size
    const sizeCounts = sizes.reduce((acc, size) => {
      acc[size] = (acc[size] || 0) + 1;
      return acc;
    }, {});

    return (
      <div className="ml-3">
        <button
          type="button"
          onClick={() =>
            type === "childAbove5"
              ? setShowChildAbove5TShirtModal(true)
              : setShowChildBelow5TShirtModal(true)
          }
          style={{
            padding: "6px 12px",
            backgroundColor: "rgba(255,255,255,0.2)",
            border: "1px solid rgba(255,255,255,0.3)",
            color: "#fff",
            borderRadius: "5px",
            fontSize: "14px",
            cursor: "pointer",
            width: "100%",
            textAlign: "left",
            minWidth: "100px",
          }}
        >
          {sizes.length === 0 ? (
            `Select ${count} T-Shirt(s)`
          ) : (
            <div>
              <div>
                Sizes:{" "}
                {Object.entries(sizeCounts)
                  .map(([size, count]) => `${size}(${count})`)
                  .join(", ")}
              </div>
              <div style={{ fontSize: "10px", opacity: 0.8 }}>
                {sizes.length} of {count} selected
              </div>
            </div>
          )}
        </button>
      </div>
    );
  };

  return (
    <div className="form-container px-4">
      <h2 className="form-title m-0">Event Registration (Save Only)</h2>
      <h5
        style={{ textAlign: "center", color: "#58db8c", marginBottom: "20px" }}
      >
        KGC'97 Day 2026
      </h5>

      {showSuccess && (
        <div className="success-message" style={{
          backgroundColor: "#d4edda",
          color: "#155724",
          padding: "12px",
          borderRadius: "5px",
          marginBottom: "20px",
          textAlign: "center",
          fontWeight: "bold"
        }}>
          🎉 Registration {existingRegistration ? "updated" : "saved"} successfully! (No payment processed)
        </div>
      )}

      {showError && (
        <div className="error-message" style={{
          backgroundColor: "#f8d7da",
          color: "#721c24",
          padding: "12px",
          borderRadius: "5px",
          marginBottom: "20px",
          textAlign: "center",
          fontWeight: "bold"
        }}>
          ❌ Error: {errorMessage}
        </div>
      )}

      {/* Mobile Search Section */}
      {!showForm && (
        <div className="search-section mb-6">
          <div className="text-center mb-2">
            <p className="text-gray-300 text-white text-lg">
              Enter your 11 digits mobile number
            </p>
          </div>

          <div className="row justify-content-center">
            <div className="col-12 col-md-8 col-lg-6">
              <div className="input-group input-group-lg">
                <input
                  type="text"
                  className="form-control bg-light text-dark border-green-500"
                  placeholder="01XXXXXXXX"
                  value={searchMobile}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");
                    if (value.length <= 11) {
                      setSearchMobile(value);
                      setSearchError("");
                    }
                  }}
                  maxLength="11"
                  style={{ height: "50px" }}
                />
                <button
                  className="btn btn-search d-flex align-items-center gap-2"
                  onClick={handleSearchRegistration}
                  disabled={searchLoading || !searchMobile.trim()}
                  style={{ height: "50px", width: "60px", padding: "0 15px" }}
                  title="Search"
                >
                  {searchLoading ? (
                    <span className="spinner-border spinner-border-sm" />
                  ) : (
                    <ArrowRight size={24} />
                  )}
                </button>
              </div>
              {searchError && (
                <div className="text-danger small mt-2 text-center">
                  {searchError}
                </div>
              )}
            </div>
          </div>
          
          {/* <div className="text-center mt-3">
            <button
              type="button"
              className="btn btn-outline-info"
              onClick={handleViewRegistrations}
            >
              View All Registrations
            </button>
          </div> */}
        </div>
      )}

      {/* Show form after search */}
      {showForm && (
        <form onSubmit={(e) => e.preventDefault()}>
          {/* Existing Registration Notification */}
          {existingRegistration && (
            <div className="alert alert-info mb-4">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <strong>✓ Registration Found</strong>
                  <div className="small">
                    {existingRegistration.paymentStatus === "Paid" || existingRegistration.paymentStatus === "Success"
                      ? "This registration is already paid. You can still update it (save only, no payment)."
                      : "You can update this registration."}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Existing Directory Notification */}
          {existingDirectory && !existingRegistration && (
            <div className="alert alert-secondary mb-4">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <strong>📋 Directory Entry Found</strong>
                  <div className="small">
                    Found directory entry for this mobile. Some fields are
                    pre-filled.
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* New Entry Notification */}
          {isNewEntry && !existingRegistration && !existingDirectory && (
            <div className="alert alert-warning mb-4">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <strong>➕ Create New Registration</strong>
                  <div className="small">
                    No existing record found for mobile: {originalMobile}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Mobile Field - Fixed display */}
          <div className="form-group">
            <label className="form-label">Mobile Number*</label>
            <div className="mobile-display">
              <div
                style={{
                  padding: "12px",
                  backgroundColor: "#e9ecef",
                  color: "#495057",
                  borderRadius: "5px",
                  border: "1px solid #dee2e6",
                  fontSize: "16px",
                  fontWeight: "bold",
                }}
              >
                {originalMobile || "No mobile number"}
              </div>
              <input type="hidden" name="mobile" value={originalMobile || ""} />
            </div>
            {errors.mobile && (
              <div className="error-message">{errors.mobile}</div>
            )}
          </div>

          {/* Name */}
          <div className="form-group">
            <label className="form-label">Name of Participant *</label>
            <input
              type="text"
              className="form-input"
              placeholder="Enter your full name"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              style={
                existingRegistration?.paymentStatus === "Paid" || existingRegistration?.paymentStatus === "Success"
                  ? { backgroundColor: "#e9ecef" }
                  : {}
              }
            />
            {errors.name && <div className="error-message">{errors.name}</div>}
          </div>

          {/* Batch */}
          <div style={{ width: "100%", position: "relative" }}>
            <label className="form-label">Group *</label>
            <select
              value={formData.batch}
              onChange={(e) => handleInputChange("batch", e.target.value)}
              style={{
                width: "100%",
                padding: "8px 15px",
                borderRadius: "0px",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                background:
                  existingRegistration?.paymentStatus === "Paid" || existingRegistration?.paymentStatus === "Success"
                    ? "#e9ecef"
                    : "rgba(255,255,255,0.1)",
                color:
                  existingRegistration?.paymentStatus === "Paid" || existingRegistration?.paymentStatus === "Success"
                    ? "#495057"
                    : "white",
                fontSize: "1rem",
                appearance: "none",
                WebkitAppearance: "none",
                MozAppearance: "none",
                backdropFilter: "blur(8px)",
                transition: "all 0.3s ease",
                backgroundImage: `url("data:image/svg+xml,%3Csvg fill='${
                  existingRegistration?.paymentStatus === "Paid" || existingRegistration?.paymentStatus === "Success"
                    ? "#495057"
                    : "white"
                }' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M5.23 7.21a.75.75 0 011.06 0L10 10.92l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.23 8.27a.75.75 0 010-1.06z'/%3E%3C/svg%3E")`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 12px center",
                backgroundSize: "1rem",
                marginBottom: "15px",
              }}
            >
              <option
                value=""
                style={{ backgroundColor: "#cffae0", color: "black" }}
              >
                Select your group
              </option>
              {Object.keys(batchPricing).map((batch) => (
                <option
                  key={batch}
                  value={batch}
                  style={{ backgroundColor: "#cffae0", color: "black" }}
                >
                  {batch}
                </option>
              ))}
            </select>
            {errors.batch && <div className="error-message">{errors.batch}</div>}
          </div>

          {/* Email */}
          <div className="form-group">
            <label className="form-label">Email </label>
            <input
              type="email"
              className="form-input"
              placeholder="your.email@example.com"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              style={
                existingRegistration?.paymentStatus === "Paid" || existingRegistration?.paymentStatus === "Success"
                  ? { backgroundColor: "#e9ecef", color: "#495057" }
                  : {}
              }
            />
            {errors.email && (
              <div className="error-message">{errors.email}</div>
            )}
          </div>

          {/* Gender Selection */}
          <div className="form-group">
            <label className="form-label">Select Gender </label>
            <div
              style={{
                display: "flex",
                gap: "20px",
                color: "white",
                marginBottom: "15px",
              }}
            >
              <label
                style={{ display: "flex", alignItems: "center", gap: "5px" }}
              >
                <input
                  type="radio"
                  name="gender"
                  value="Male"
                  checked={gender === "Male"}
                  onChange={(e) => setGender(e.target.value)}
                />
                Male
              </label>
              <label
                style={{ display: "flex", alignItems: "center", gap: "5px" }}
              >
                <input
                  type="radio"
                  name="gender"
                  value="Female"
                  checked={gender === "Female"}
                  onChange={(e) => setGender(e.target.value)}
                />
                Female
              </label>
            </div>
          </div>

          {/* T-shirt Size */}
          <div style={{ width: "100%", position: "relative" }}>
            <label className="form-label">T-Shirt Size </label>
            <select
              value={formData.tshirtSize}
              onChange={(e) => handleInputChange("tshirtSize", e.target.value)}
              style={{
                width: "100%",
                padding: "8px 15px",
                borderRadius: "0px",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                background:
                  existingRegistration?.paymentStatus === "Paid" || existingRegistration?.paymentStatus === "Success"
                    ? "#e9ecef"
                    : "rgba(255,255,255,0.1)",
                color:
                  existingRegistration?.paymentStatus === "Paid" || existingRegistration?.paymentStatus === "Success"
                    ? "#495057"
                    : "white",
                fontSize: "1rem",
                appearance: "none",
                WebkitAppearance: "none",
                MozAppearance: "none",
                backdropFilter: "blur(8px)",
                transition: "all 0.3s ease",
                backgroundImage: `url("data:image/svg+xml,%3Csvg fill='${
                  existingRegistration?.paymentStatus === "Paid" || existingRegistration?.paymentStatus === "Success"
                    ? "#495057"
                    : "white"
                }' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M5.23 7.21a.75.75 0 011.06 0L10 10.92l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.23 8.27a.75.75 0 010-1.06z'/%3E%3C/svg%3E")`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 12px center",
                backgroundSize: "1rem",
                marginBottom: "15px",
              }}
            >
              <option
                value=""
                style={{ backgroundColor: "#cffae0", color: "black" }}
              >
                Select your T-Shirt Size
              </option>
              {tShirtSize.map((size) => (
                <option
                  key={size}
                  value={size}
                  style={{ backgroundColor: "#cffae0", color: "black" }}
                >
                  {size}
                </option>
              ))}
            </select>
            {errors.tshirtSize && (
              <div className="error-message">{errors.tshirtSize}</div>
            )}
          </div>

          {/* Accompany Section - UPDATED */}
          <div className="form-group">
            <label className="form-label">Accompanying Persons</label>
            <div
              className="accompany-section"
              style={{
                padding: "15px",
                borderRadius: "12px",
                background: "rgba(255,255,255,0.2)",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                opacity:
                  existingRegistration?.paymentStatus === "Paid" || existingRegistration?.paymentStatus === "Success" ? 0.6 : 1,
              }}
            >
              {accompanyCategories.map((category) => (
                <div key={category.key}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: "15px",
                      color: "#fff",
                      flexWrap: "wrap",
                    }}
                  >
                    <span style={{ flex: 1 }}>
                      {category.label} (
                      {(() => {
                        const amount =
                          batchPricing[formData.batch || "Arts"]?.[
                            category.key === "spouse"
                              ? "spouseFee"
                              : category.key === "guest"
                              ? "guestFee"
                              : category.key === "driver"
                              ? "driverFee"
                              : category.key === "childAbove5"
                              ? "above5Fee"
                              : "below5Fee"
                          ] || 0;

                        return amount > 0 ? `Tk. ${amount}` : "Free";
                      })()}
                      )
                    </span>

                    <div className="d-flex flex-column flex-md-row align-items-center justify-content-between ">
                      <div className="d-flex align-items-center gap-2">
                        <button
                          className="btn"
                          style={{
                            backgroundColor: "rgba(255,255,255,0.2)",
                            border: "1px solid rgba(255,255,255,0.3)",
                            color: "#fff",
                            width: "30px",
                            height: "30px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                          type="button"
                          onClick={() => handleAccompanyChange(category.key, -1)}
                        >
                          -
                        </button>
                        <span style={{ width: "40px", textAlign: "center" }}>
                          {formData.accompany[category.key]}
                        </span>
                        <button
                          className="btn"
                          style={{
                            backgroundColor: "rgba(255,255,255,0.2)",
                            border: "1px solid rgba(255,255,255,0.3)",
                            color: "#fff",
                            width: "30px",
                            height: "30px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                          type="button"
                          onClick={() => handleAccompanyChange(category.key, 1)}
                        >
                          +
                        </button>
                      </div>
                      
                      {/* Only show T-shirt size button for children categories */}
                      {category.showTshirt && (
                        <div className="w-100 w-md-auto mt-2 mt-md-0">
                          {renderSizeButton(category.key)}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Display validation errors for children T-shirt sizes */}
                  {formData.accompany[category.key] > 0 &&
                    category.showTshirt && (
                      <div style={{ marginBottom: "10px" }}>
                        {category.key === "childAbove5" &&
                          errors.childAbove5TshirtSize && (
                            <div
                              className="error-message"
                              style={{
                                color: "#ff6b6b",
                                marginTop: "5px",
                                fontSize: "12px",
                              }}
                            >
                              {errors.childAbove5TshirtSize}
                            </div>
                          )}
                        {category.key === "childBelow5" &&
                          errors.childBelow5TshirtSize && (
                            <div
                              className="error-message"
                              style={{
                                color: "#ff6b6b",
                                marginTop: "5px",
                                fontSize: "12px",
                              }}
                            >
                              {errors.childBelow5TshirtSize}
                            </div>
                          )}
                      </div>
                    )}
                </div>
              ))}
            </div>
          </div>

          {/* T-Shirt Size Modals */}
          <TShirtSizeModal
            isOpen={showChildAbove5TShirtModal}
            onClose={() => setShowChildAbove5TShirtModal(false)}
            onSelect={(size, action) =>
              handleTShirtSizeModalSelect("childAbove5TshirtSize", size, action)
            }
            selectedSizesStr={formData.childAbove5TshirtSize}
            title="Select T-Shirt Sizes for Child Above 5"
            maxSelections={formData.accompany.childAbove5}
            availableSizes={childAbove5TShirtSize}
            onClear={() => handleClearSelection("childAbove5TshirtSize")}
          />

          <TShirtSizeModal
            isOpen={showChildBelow5TShirtModal}
            onClose={() => setShowChildBelow5TShirtModal(false)}
            onSelect={(size, action) =>
              handleTShirtSizeModalSelect("childBelow5TshirtSize", size, action)
            }
            selectedSizesStr={formData.childBelow5TshirtSize}
            title="Select T-Shirt Sizes for Child Below 5"
            maxSelections={formData.accompany.childBelow5}
            availableSizes={childBelow5TShirtSize}
            onClear={() => handleClearSelection("childBelow5TshirtSize")}
          />

          {/* Payment Information Section - NEW */}
          {/* Payment Information Section - NEW */}
<div className="form-group mt-4">
  <label
    className="form-label"
    style={{
      fontSize: "1.2rem",
      fontWeight: "bold",
      color: "#58db8c",
    }}
  >
    Payment Information
  </label>
  <div
    style={{
      padding: "15px",
      borderRadius: "12px",
      background: "rgba(255,255,255,0.15)",
      border: "1px solid rgba(255, 255, 255, 0.2)",
    }}
  >
    {/* Payment Status Toggle Button */}
    <div className="form-group">
      <label className="form-label">Payment Status</label>
      <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
        <button
          type="button"
          onClick={() => handleInputChange("paymentStatus", "Success")}
          style={{
            padding: "10px 20px",
            borderRadius: "5px",
            border: "none",
            backgroundColor: formData.paymentStatus === "Success" ? "#28a745" : "#6c757d",
            color: "white",
            cursor: "pointer",
            fontWeight: "bold",
            flex: 1,
          }}
        >
          Success
        </button>
        <button
          type="button"
          onClick={() => handleInputChange("paymentStatus", "Pending")}
          style={{
            padding: "10px 20px",
            borderRadius: "5px",
            border: "none",
            backgroundColor: formData.paymentStatus === "Pending" ? "#ffc107" : "#6c757d",
            color: formData.paymentStatus === "Pending" ? "black" : "white",
            cursor: "pointer",
            fontWeight: "bold",
            flex: 1,
          }}
        >
          Pending
        </button>
      </div>
      {formData.paymentStatus && (
        <div style={{ marginTop: "8px", fontSize: "14px", color: "#fff" }}>
          Current Status: <strong>{formData.paymentStatus}</strong>
        </div>
      )}
    </div>

    {/* Transaction ID */}
    <div className="form-group">
      <label className="form-label">Transaction ID</label>
      <input
        type="text"
        className="form-input"
        placeholder="Enter Transaction ID"
        value={formData.transactionId}
        onChange={(e) =>
          handleInputChange("transactionId", e.target.value)
        }
        style={{
          backgroundColor:
            existingRegistration?.paymentStatus === "Paid"
              ? "#e9ecef"
              : "rgba(255,255,255,0.1)",
          color: "white",
        }}
      />
      {errors.transactionId && (
        <div className="error-message">{errors.transactionId}</div>
      )}
    </div>

    {/* Payment Method */}
    <div style={{ width: "100%", position: "relative" }}>
      <label className="form-label">Payment Method</label>
      <select
        value={formData.paymentMethod}
        onChange={(e) =>
          handleInputChange("paymentMethod", e.target.value)
        }
        style={{
          width: "100%",
          padding: "8px 15px",
          borderRadius: "0px",
          border: "1px solid rgba(255, 255, 255, 0.2)",
          background: "rgba(255,255,255,0.1)",
          color: "white",
          fontSize: "1rem",
          appearance: "none",
          WebkitAppearance: "none",
          MozAppearance: "none",
          backdropFilter: "blur(8px)",
          transition: "all 0.3s ease",
          backgroundImage: `url("data:image/svg+xml,%3Csvg fill='white' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M5.23 7.21a.75.75 0 011.06 0L10 10.92l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.23 8.27a.75.75 0 010-1.06z'/%3E%3C/svg%3E")`,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "right 12px center",
          backgroundSize: "1rem",
          marginBottom: "15px",
        }}
      >
        {paymentMethodOptions.map((option) => (
          <option
            key={option.value}
            value={option.value}
            style={{ backgroundColor: "#cffae0", color: "black" }}
          >
            {option.label}
          </option>
        ))}
      </select>
    </div>

    {/* Payment Person */}
    <div className="form-group">
      <label className="form-label">
        Payment Person (To whom they paid)
      </label>
      <input
        type="text"
        className="form-input"
        placeholder="Enter name of person who received payment"
        value={formData.orderId}
        onChange={(e) => handleInputChange("orderId", e.target.value)}
        style={{
          backgroundColor:
            existingRegistration?.paymentStatus === "Paid"
              ? "#e9ecef"
              : "rgba(255,255,255,0.1)",
          color: "white",
        }}
      />
      {errors.orderId && (
        <div className="error-message">{errors.orderId}</div>
      )}
    </div>

    {/* Registration Date */}
    <div className="form-group">
      <label className="form-label">Registration Date</label>
      <input
        type="date"
        className="form-input"
        value={formData.registrationDate}
        onChange={(e) =>
          handleInputChange("registrationDate", e.target.value)
        }
        style={{
          backgroundColor:
            existingRegistration?.paymentStatus === "Paid"
              ? "#e9ecef"
              : "rgba(255,255,255,0.1)",
          color: "white",
        }}
      />
      {errors.registrationDate && (
        <div className="error-message">{errors.registrationDate}</div>
      )}
    </div>
  </div>
</div>
          {/* <div className="form-group mt-4">
            <label className="form-label" style={{ fontSize: "1.2rem", fontWeight: "bold", color: "#58db8c" }}>
              Payment Information
            </label>
            <div
              style={{
                padding: "15px",
                borderRadius: "12px",
                background: "rgba(255,255,255,0.15)",
                border: "1px solid rgba(255, 255, 255, 0.2)",
              }}
            >
            
              <div className="form-group">
                <label className="form-label">Transaction ID</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Enter Transaction ID"
                  value={formData.transactionId}
                  onChange={(e) => handleInputChange("transactionId", e.target.value)}
                  style={{
                    backgroundColor: existingRegistration?.paymentStatus === "Paid" ? "#e9ecef" : "rgba(255,255,255,0.1)",
                    color: "white",
                  }}
                />
                {errors.transactionId && <div className="error-message">{errors.transactionId}</div>}
              </div>

              <div style={{ width: "100%", position: "relative" }}>
                <label className="form-label">Payment Method</label>
                <select
                  value={formData.paymentMethod}
                  onChange={(e) => handleInputChange("paymentMethod", e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px 15px",
                    borderRadius: "0px",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    background: "rgba(255,255,255,0.1)",
                    color: "white",
                    fontSize: "1rem",
                    appearance: "none",
                    WebkitAppearance: "none",
                    MozAppearance: "none",
                    backdropFilter: "blur(8px)",
                    transition: "all 0.3s ease",
                    backgroundImage: `url("data:image/svg+xml,%3Csvg fill='white' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M5.23 7.21a.75.75 0 011.06 0L10 10.92l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.23 8.27a.75.75 0 010-1.06z'/%3E%3C/svg%3E")`,
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "right 12px center",
                    backgroundSize: "1rem",
                    marginBottom: "15px",
                  }}
                >
                  {paymentMethodOptions.map((option) => (
                    <option
                      key={option.value}
                      value={option.value}
                      style={{ backgroundColor: "#cffae0", color: "black" }}
                    >
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Payment Person (To whom they paid)</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Enter name of person who received payment"
                  value={formData.orderId}
                  onChange={(e) => handleInputChange("orderId", e.target.value)}
                  style={{
                    backgroundColor: existingRegistration?.paymentStatus === "Paid" ? "#e9ecef" : "rgba(255,255,255,0.1)",
                    color: "white",
                  }}
                />
                {errors.orderId && <div className="error-message">{errors.orderId}</div>}
              </div>

              <div className="form-group">
                <label className="form-label">Registration Date</label>
                <input
                  type="date"
                  className="form-input"
                  value={formData.registrationDate}
                  onChange={(e) => handleInputChange("registrationDate", e.target.value)}
                  style={{
                    backgroundColor: existingRegistration?.paymentStatus === "Paid" ? "#e9ecef" : "rgba(255,255,255,0.1)",
                    color: "white",
                  }}
                />
                {errors.registrationDate && <div className="error-message">{errors.registrationDate}</div>}
              </div>

             
            </div>
          </div> */}

          {/* Total */}
          <div
            className="flex-1 md:flex-auto flex justify-center items-center gap-2 px-6 py-2.5 bg-green-500 hover:bg-green-600 text-white rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontSize: "1.5rem",
              marginTop: "20px",
            }}
          >
            <span>Total amount:</span>
            <span className="amount-title">৳{calculateTotal()}</span>
          </div>

          {/* Additional Payment Section - Informational only */}
          {showAdditionalPayment && existingRegistration?.paymentStatus === "Success" && (
            <div
              className="mt-4 p-4 bg-yellow-100 border border-yellow-400 rounded-xl"
              style={{
                background: "rgba(255, 193, 7, 0.1)",
                border: "1px solid #ffc107",
              }}
            >
              <div className="text-yellow-300 font-bold mb-2">
                ⚠️ Additional Amount Would Be Required
              </div>
              <div className="text-white mb-3">
                You have added more accompanying persons. Additional payment of ৳{additionalPaymentAmount} would be required for payment processing.
              </div>
              <div
                className="flex justify-between items-center text-white mb-3 p-2 bg-black/20 rounded"
              >
                <span>Originally Paid:</span>
                <span className="font-bold">৳{existingRegistration.totalAmount || 0}</span>
              </div>
              <div
                className="flex justify-between items-center text-white mb-3 p-2 bg-black/20 rounded"
              >
                <span>New Total:</span>
                <span className="font-bold">৳{calculateTotal()}</span>
              </div>
              <div
                className="flex justify-between items-center text-yellow-300 font-bold text-lg mb-4 p-2 bg-black/30 rounded"
              >
                <span>Additional Amount:</span>
                <span>৳{additionalPaymentAmount}</span>
              </div>
            </div>
          )}

          {/* Buttons */}
          <div className="button-group flex gap-4 mt-6">
            <button
              type="button"
              className="flex-1 md:flex-auto flex justify-center items-center gap-2 px-6 py-2.5 bg-blue-500 hover:bg-blue-600 text-white text-bold rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleSaveOnly}
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="spinner-border spinner-border-sm" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  Saving...
                </>
              ) : existingRegistration ? (
                "Already Saved"
              ) : (
                "Save Registration"
              )}
            </button>

            <button
              type="button"
              className="flex justify-center items-center gap-2 px-4 py-2 text-white rounded-xl border border-white/20 bg-white/10 hover:bg-white/20 transition"
              onClick={handleResetSearch}
            >
              <X size={18} />
              New Search
            </button>

            <button
              type="button"
              className="flex justify-center items-center gap-2 px-4 py-2 text-white rounded-xl border border-white/20 bg-white/10 hover:bg-white/20 transition"
              onClick={handleHomeClick}
            >
              <Home size={18} />
              Go To Home
            </button>
          </div>
        </form>
      )}

      {/* Show only home button if not showing form */}
      {!showForm && (
        <div className="flex justify-center text-center mt-4">
          <button
            type="button"
            className="flex items-center gap-2 px-4 py-2 text-white rounded-xl border border-white/20 bg-white/10 hover:bg-white/20 transition"
            onClick={handleHomeClick}
          >
            <Home size={18} />
            Go To Home
          </button>
        </div>
      )}

      {/* Payment Info - Kept contact info */}
      {showForm && (
        <>
          <div className="hero-text mt-8 flex justify-center items-center">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              style={{ fontSize: "16px", color: "#fff" }}
            >
              পেমেন্ট সংক্রান্ত টেকনিক্যাল সমস্যায় যোগাযোগ করুন : 01711065316
            </motion.p>
          </div>
        </>
      )}
    </div>
  );
};

export default SaveOnlyRegistrationPage;